import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { PaperclipIcon } from "lucide-react";
import { toast } from "sonner";
import {
  DEFAULT_SUPPORTED_FILE_TYPES,
  useImageUploader,
} from "@/components/AppHeader/FileUpload";
import { useCanvasId } from "@/utils/utils";

interface DragHoverTargetsProps {
  onUploadFiles: (files: File[]) => void;
  onUploadToCodebase: (files: File[]) => void;
  isContextDisabled?: boolean;
  pastedFiles?: File[];
}

const DragHoverTargets: React.FC<DragHoverTargetsProps> = ({
  onUploadFiles,
  onUploadToCodebase,
  isContextDisabled = false,
  pastedFiles = [],
}) => {
  const canvasId = useCanvasId();

  const { FileUploadComponent, FileUploadToCodebaseComponent } =
    useImageUploader();

  const [dragTarget, setDragTarget] = useState<"context" | "codebase" | null>(
    null
  );

  const handleDragEnter =
    (target: "context" | "codebase") => (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes("Files")) {
        setDragTarget(target);
      }
    };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only clear if we're leaving to go outside both zones
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Check if we're moving to the other drop zone by seeing if we're still within the parent container
    const parentRect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (
      parentRect &&
      (x < parentRect.left ||
        x > parentRect.right ||
        y < parentRect.top ||
        y > parentRect.bottom)
    ) {
      setDragTarget(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropOnTarget =
    (target: "context" | "codebase") => (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        let fileArray: File[];

        if (target === "context") {
          // For context uploads, only allow supported file types
          const allFiles = Array.from(files);
          fileArray = allFiles.filter((file) =>
            DEFAULT_SUPPORTED_FILE_TYPES.includes(file.type)
          );

          // Show warning toast for unsupported files
          const unsupportedFiles = allFiles.filter(
            (file) => !DEFAULT_SUPPORTED_FILE_TYPES.includes(file.type)
          );

          if (unsupportedFiles.length > 0) {
            const fileNames = unsupportedFiles.map((f) => f.name).join(", ");
            toast.warning(
              `Files not supported for AI context: ${fileNames}.</br> \n Supported types: PDF, images.`
            );
          }
        } else {
          // For codebase uploads, allow all file types
          fileArray = Array.from(files);
        }

        if (fileArray.length > 0) {
          if (target === "context") {
            onUploadFiles(fileArray);
          } else {
            onUploadToCodebase(fileArray);
          }
        }
      }

      setDragTarget(null);
    };

  const handleClickTarget = (target: "context" | "codebase") => () => {
    if (pastedFiles.length > 0) {
      if (target === "context") {
        if (!isContextDisabled) {
          onUploadFiles(pastedFiles);
        }
      } else {
        onUploadToCodebase(pastedFiles);
      }
    }
  };

  const handleKeyDown =
    (target: "context" | "codebase") => (event: React.KeyboardEvent) => {
      // Only handle intentional keyboard activation (Enter/Space)
      // Don't auto-trigger on any other keys
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        handleClickTarget(target)();
      }
    };

  return (
    <div className="flex gap-2 items-center justify-center w-full h-[100px]">
      <div
        className={cn(
          "transition-all flex items-center justify-center rounded-lg border-2 border-dashed h-full w-full",
          dragTarget === "context"
            ? "border-primary-0 bg-background-tertiary"
            : "",
          isContextDisabled && "opacity-50 cursor-not-allowed",
          pastedFiles.length > 0 &&
            !isContextDisabled &&
            "cursor-pointer hover:border-primary-0 hover:bg-background-tertiary"
        )}
        onDragEnter={handleDragEnter("context")}
        onDragLeave={handleDragLeave}
        onDragOver={isContextDisabled ? undefined : handleDragOver}
        onDrop={isContextDisabled ? undefined : handleDropOnTarget("context")}
        onClick={
          pastedFiles.length > 0 ? handleClickTarget("context") : undefined
        }
        onKeyDown={
          pastedFiles.length > 0 ? handleKeyDown("context") : undefined
        }
        tabIndex={-1}
        role={
          pastedFiles.length > 0 && !isContextDisabled ? "button" : undefined
        }
      >
        <div
          className="flex flex-col justify-center items-center gap-1 text-xs h-full w-full group"
          data-tooltip-id="tooltip"
          data-tooltip-content={
            isContextDisabled
              ? "Only images and PDF supported for context"
              : undefined
          }
        >
          {pastedFiles.length > 0 ? (
            <>
              <PaperclipIcon className="size-4 text-icons-muted group-hover:text-icons-primary" />
              <span className="text-center px-1">Upload context for AI</span>
            </>
          ) : (
            <FileUploadComponent layout="column" />
          )}
        </div>
      </div>

      {canvasId && ( // TODO: I gave up on getting pending uploads to work on for FTUE, it's probably an easy fix but the process should have been as follows:
        // 1. Pending uploads are stored in the state
        // 2. wait for canvas to load
        // 3. upload pending uploads
        // 4. aiGeneratePRD is called with existing context (and pending uploads)
        <div
          className={cn(
            "transition-all flex items-center justify-center rounded-lg border-2 border-dashed h-full w-full",
            dragTarget === "codebase"
              ? "border-primary-0 bg-background-tertiary"
              : "",
            pastedFiles.length > 0 &&
              "cursor-pointer hover:border-primary-0 hover:bg-background-tertiary"
          )}
          onDragEnter={handleDragEnter("codebase")}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDropOnTarget("codebase")}
          onClick={
            pastedFiles.length > 0 ? handleClickTarget("codebase") : undefined
          }
          onKeyDown={
            pastedFiles.length > 0 ? handleKeyDown("codebase") : undefined
          }
          tabIndex={-1}
          role={pastedFiles.length > 0 ? "button" : undefined}
        >
          <div className="flex flex-col justify-center items-center gap-1 text-xs h-full w-full group">
            {pastedFiles.length > 0 ? (
              <>
                <PaperclipIcon className="size-4 text-icons-muted group-hover:text-icons-primary" />
                <span className="text-center px-1">Upload to Codebase</span>
              </>
            ) : (
              <FileUploadToCodebaseComponent layout="column" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DragHoverTargets;
